// /app/services/error_enums/user_error.tsx

/**
 * Backend (C#) error codes for User service.
 * Use string enum values to match server payloads reliably.
 */
export enum UserServiceErrorCode {
    USER_ALREADY_ACTIVATED = "USER_ALREADY_ACTIVATED",
    INVALID_REFRESH_TOKEN = "INVALID_REFRESH_TOKEN",
    INVALID_ACCESS_TOKEN = "INVALID_ACCESS_TOKEN",
    REFRESH_TOKEN_NOT_EXISTS = "REFRESH_TOKEN_NOT_EXISTS",
    USER_NOT_EXISTS = "USER_NOT_EXISTS",
    CREDENTIAL_WRONG = "CREDENTIAL_WRONG",
    NO_PASSWORD = "NO_PASSWORD",
    USER_MOBILE_NOT_ACTIVATED = "USER_MOBILE_NOT_ACTIVATED",
    USER_EMAIL_NOT_ACTIVATED = "USER_EMAIL_NOT_ACTIVATED",
    USER_EMAIL_ALREADY_EXISTS = "USER_EMAIL_ALREADY_EXISTS",
    USER_NOT_ACTIVATED = "USER_NOT_ACTIVATED",
    LINK_EXPIRED = "LINK_EXPIRED",
    LINK_INVALID = "LINK_INVALID",
    SUCCESS = "SUCCESS",
    UNABLE_TO_DELETE_PROFILE_IMAGE = "UNABLE_TO_DELETE_PROFILE_IMAGE",
    PROFILE_IMAGE_NOT_FOUND = "PROFILE_IMAGE_NOT_FOUND",
    GOOGLE_TOKEN_EXPIRED = "GOOGLE_TOKEN_EXPIRED",
    GOOGLE_TOKEN_MISSING = "GOOGLE_TOKEN_MISSING",
    INVALID_AUTH_PROVIDER = "INVALID_AUTH_PROVIDER",
    TRY_TO_LOGIN_WITH_GOOGLE = "TRY_TO_LOGIN_WITH_GOOGLE",
    TRY_TO_LOGIN_WITH_APPLE = "TRY_TO_LOGIN_WITH_APPLE",
    INVALID_PAYLOAD = "INVALID_PAYLOAD",
    OTP_INVALID = "OTP_INVALID",
    USER_EMAIL_ALREADY_VERIFIED = "USER_EMAIL_ALREADY_VERIFIED",
    USER_MOBILE_ALREADY_VERIFIED = "USER_MOBILE_ALREADY_VERIFIED",
    USER_MOBILE_ALREADY_EXISTS = "USER_MOBILE_ALREADY_EXISTS",
    USER_PROFILE_NOT_UPDATED = "USER_PROFILE_NOT_UPDATED",
    USER_AUTH_3_ALREADY_EXISTS = "USER_AUTH_3_ALREADY_EXISTS",
    GOOGLE_TOKEN_INVALID = "GOOGLE_TOKEN_INVALID",
    UNAUTHENTICATED_USER = "UNAUTHENTICATED_USER",
    FAILED_TO_UPLOAD_FILE = "FAILED_TO_UPLOAD_FILE",
    FILE_NOT_FOUND = "FILE_NOT_FOUND",
    INVALID_REASON = "INVALID_REASON",
    SMS_FAILURE = "SMS_FAILURE",

    // Note: backend may send this exact camelCase key
    userIdByAdminRequired = "userIdByAdminRequired",

    UNKNOWN_ERROR = "UNKNOWN_ERROR",
}

export const userServiceErrorMessages: Record<UserServiceErrorCode, string> = {
    [UserServiceErrorCode.USER_ALREADY_ACTIVATED]: "Your account is already activated.",
    [UserServiceErrorCode.INVALID_REFRESH_TOKEN]: "Your session has expired. Please sign in again.",
    [UserServiceErrorCode.INVALID_ACCESS_TOKEN]: "Your session has expired. Please sign in again.",
    [UserServiceErrorCode.REFRESH_TOKEN_NOT_EXISTS]: "Your session has expired. Please sign in again.",
    [UserServiceErrorCode.USER_NOT_EXISTS]: "We couldn’t find an account with these details.",
    [UserServiceErrorCode.CREDENTIAL_WRONG]: "Incorrect username or password.",
    [UserServiceErrorCode.NO_PASSWORD]: "No password is set for this account. Please reset your password.",
    [UserServiceErrorCode.USER_MOBILE_NOT_ACTIVATED]: "Your mobile number is not verified. Please verify it to continue.",
    [UserServiceErrorCode.USER_EMAIL_NOT_ACTIVATED]: "Your email address is not verified. Please verify it to continue.",
    [UserServiceErrorCode.USER_EMAIL_ALREADY_EXISTS]: "This email address is already in use.",
    [UserServiceErrorCode.USER_NOT_ACTIVATED]: "Your account is not activated yet. Please check your email for the activation link.",
    [UserServiceErrorCode.LINK_EXPIRED]: "This link has expired. Please request a new one.",
    [UserServiceErrorCode.LINK_INVALID]: "This link is invalid. Please request a new one.",
    [UserServiceErrorCode.SUCCESS]: "Success.",
    [UserServiceErrorCode.UNABLE_TO_DELETE_PROFILE_IMAGE]: "We couldn’t remove your profile image. Please try again.",
    [UserServiceErrorCode.PROFILE_IMAGE_NOT_FOUND]: "Profile image not found.",
    [UserServiceErrorCode.GOOGLE_TOKEN_EXPIRED]: "Google session expired. Please try signing in with Google again.",
    [UserServiceErrorCode.GOOGLE_TOKEN_MISSING]: "Missing Google sign-in token. Please try again.",
    [UserServiceErrorCode.INVALID_AUTH_PROVIDER]: "Invalid sign-in method. Please try a different option.",
    [UserServiceErrorCode.TRY_TO_LOGIN_WITH_GOOGLE]: "This account uses Google sign-in. Please continue with Google.",
    [UserServiceErrorCode.TRY_TO_LOGIN_WITH_APPLE]: "This account uses Apple sign-in. Please continue with Apple.",
    [UserServiceErrorCode.INVALID_PAYLOAD]: "Invalid request. Please try again.",
    [UserServiceErrorCode.OTP_INVALID]: "Invalid verification code. Please try again.",
    [UserServiceErrorCode.USER_EMAIL_ALREADY_VERIFIED]: "Your email is already verified.",
    [UserServiceErrorCode.USER_MOBILE_ALREADY_VERIFIED]: "Your mobile number is already verified.",
    [UserServiceErrorCode.USER_MOBILE_ALREADY_EXISTS]: "This mobile number is already in use.",
    [UserServiceErrorCode.USER_PROFILE_NOT_UPDATED]: "We couldn’t update your profile. Please try again.",
    [UserServiceErrorCode.USER_AUTH_3_ALREADY_EXISTS]: "This sign-in method is already linked to your account.",
    [UserServiceErrorCode.GOOGLE_TOKEN_INVALID]: "Invalid Google token. Please try again.",
    [UserServiceErrorCode.UNAUTHENTICATED_USER]: "Please sign in to continue.",
    [UserServiceErrorCode.FAILED_TO_UPLOAD_FILE]: "File upload failed. Please try again.",
    [UserServiceErrorCode.FILE_NOT_FOUND]: "File not found.",
    [UserServiceErrorCode.INVALID_REASON]: "Invalid reason provided.",
    [UserServiceErrorCode.SMS_FAILURE]: "We couldn’t send the SMS. Please try again later.",
    [UserServiceErrorCode.userIdByAdminRequired]: "Please select a user to continue.",
    [UserServiceErrorCode.UNKNOWN_ERROR]: "Something went wrong. Please try again.",
};

export type UserServiceError =
    | UserServiceErrorCode
    | keyof typeof UserServiceErrorCode
    | string
    | undefined
    | null;

export function isUserServiceErrorCode(value: string): value is UserServiceErrorCode {
    return Object.values(UserServiceErrorCode).includes(value as UserServiceErrorCode);
}

/**
 * Returns a customer-friendly message for a backend error code.
 * Accepts raw strings from API responses.
 */
export function getUserServiceErrorMessage(code: UserServiceError): string {
    if (!code) return userServiceErrorMessages[UserServiceErrorCode.UNKNOWN_ERROR];

    const raw = String(code);

    // direct match (including camelCase key)
    if (isUserServiceErrorCode(raw)) return userServiceErrorMessages[raw];

    // common normalization for SCREAMING_SNAKE codes
    const upper = raw.toUpperCase();
    if (isUserServiceErrorCode(upper)) return userServiceErrorMessages[upper];

    return userServiceErrorMessages[UserServiceErrorCode.UNKNOWN_ERROR];
}